import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { fmtDateTime, to12h } from "@/lib/datetime";
import { Car, Mail, RefreshCw, ArrowLeft, Lock, Loader2, LogOut, FileText, FileSignature, Download, Copy, Truck, Trash2, LayoutDashboard, Send, QrCode, Star, DollarSign, Users, CalendarDays, ShieldAlert, BarChart3, UserCheck, BookOpen, Receipt, Gift, Wrench, Ban, Bell, Upload, Calculator, Check, Camera, ClipboardList, Plug, Gauge, Power, BellOff, Megaphone, Archive, Bot, RotateCcw, TrendingDown, Search, BellRing } from "lucide-react";
import CompletedRentals, { DeclinedRequests, isCompletedRental } from "@/components/admin/CompletedRentals";
import ComposeMessage from "@/components/admin/ComposeMessage";
import BulkMessage from "@/components/admin/BulkMessage";
import ScheduledMessages from "@/components/admin/ScheduledMessages";
import FleetManager from "@/components/admin/FleetManager";
import OperationsDashboard from "@/components/admin/OperationsDashboard";
import SharePanel from "@/components/admin/SharePanel";
import ReviewsManager from "@/components/admin/ReviewsManager";
import FinanceCenter from "@/components/admin/finance/FinanceCenter";
import PaymentDueCycles from "@/components/admin/PaymentDueCycles";
import ReverifyDocs from "@/components/admin/ReverifyDocs";
import FinancialsEditor from "@/components/admin/FinancialsEditor";
import { BookingPricing } from "@/components/admin/BookingPricing";
import CustomersManager from "@/components/admin/CustomersManager";
import RebookingRequests from "@/components/admin/RebookingRequests";
import AvailabilityManager from "@/components/admin/AvailabilityManager";
import RecoveryManager from "@/components/admin/RecoveryManager";
import AnalyticsManager from "@/components/admin/AnalyticsManager";
import DriversManager from "@/components/admin/DriversManager";
import CitationsManager from "@/components/admin/CitationsManager";
import AddressVerify from "@/components/admin/AddressVerify";
import RewardsManager from "@/components/admin/RewardsManager";
import AdminDocUpload, { DocFile } from "@/components/admin/AdminDocUpload";
import DamageCharge from "@/components/admin/DamageCharge";
import ChargeCard from "@/components/admin/ChargeCard";
import PaymentNudge from "@/components/admin/PaymentNudge";
import OweButton from "@/components/admin/OweButton";
import ClubPointsButton from "@/components/admin/ClubPointsButton";
import AutoRenewToggle from "@/components/admin/AutoRenewToggle";
import DailyRollingToggle from "@/components/admin/DailyRollingToggle";
import ReturnCompliancePanel from "@/components/admin/ReturnCompliancePanel";
import RevenueLeakage from "@/components/admin/RevenueLeakage";
import RiskBadge from "@/components/admin/RiskBadge";
import ExtendRental from "@/components/admin/ExtendRental";
import NotificationBell from "@/components/admin/NotificationBell";
import RentalTimeline from "@/components/admin/RentalTimeline";
import CrmStatusChip from "@/components/admin/CrmStatusChip";
import EditBookingDates from "@/components/admin/EditBookingDates";
import ReceiptDialog from "@/components/admin/ReceiptDialog";
import SwapCar from "@/components/admin/SwapCar";
import TabManual from "@/components/admin/TabManual";
import FleetMaintenance from "@/components/admin/FleetMaintenance";
import ManualBooking from "@/components/admin/ManualBooking";
import HelpManual from "@/components/admin/HelpManual";
import NotificationsHealth from "@/components/admin/NotificationsHealth";
import LifecycleStatus from "@/components/admin/LifecycleStatus";
import InsuranceVerify from "@/components/admin/InsuranceVerify";
import ImportContacts from "@/components/admin/ImportContacts";
import MarketingCenter from "@/components/admin/MarketingCenter";
import ReservationQueue from "@/components/admin/ReservationQueue";
import TaxPnL from "@/components/admin/TaxPnL";
import InspectionPanel from "@/components/admin/InspectionPanel";
import InspectionsTab from "@/components/admin/InspectionsTab";
import CheckoutPlaybook from "@/components/admin/CheckoutPlaybook";
import SystemCenter from "@/components/admin/SystemCenter";
import CommandCenter from "@/components/admin/CommandCenter";
import AdminSearch from "@/components/admin/AdminSearch";
import AiAssistant from "@/components/admin/AiAssistant";
import DailyBriefingBanner from "@/components/admin/DailyBriefingBanner";
import DnrManager from "@/components/admin/DnrManager";
import AutomationCenter from "@/components/admin/AutomationCenter";
import BookingCard from "@/components/admin/BookingCard";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "autohire_admin_token";

const Tag = ({ children, highlight }) => (
  <span className={`text-xs px-2.5 py-1 rounded-full border ${highlight ? "bg-[#F2B705] text-black border-[#F2B705] font-bold" : "border-white/15 text-white/65"}`}>{children}</span>
);

function DocImage({ path, label, token }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!path) return;
    let active = true;
    let objUrl;
    axios.get(`${API}/files/${path}`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" })
      .then((res) => { if (active) { objUrl = URL.createObjectURL(res.data); setUrl(objUrl); } })
      .catch(() => active && setErr(true));
    return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [path, token]);
  if (!path) return null;
  return (
    <a href={url || undefined} target="_blank" rel="noreferrer" className="block w-28">
      <div className="h-20 w-28 rounded-md border border-white/15 overflow-hidden bg-black flex items-center justify-center">
        {err ? <span className="text-white/30 text-xs">unavailable</span>
          : url ? <img src={url} alt={label} className="w-full h-full object-cover" />
          : <Loader2 className="animate-spin text-white/40" size={16} />}
      </div>
      <span className="text-[11px] text-white/45 mt-1 block text-center">{label}</span>
    </a>
  );
}

function LoginGate({ onAuthed }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onAuthed(data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-5">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm bg-[#0C0C0C] border border-white/10 rounded-2xl p-8">
        <div className="w-12 h-12 rounded-lg bg-[#F2B705]/10 border border-[#F2B705]/20 flex items-center justify-center mb-5">
          <Lock className="text-[#F2B705]" size={22} />
        </div>
        <h1 className="font-display font-extrabold text-2xl">Admin Access</h1>
        <p className="text-white/50 text-sm mt-1">Enter your password to view bookings.</p>
        <input
          data-testid="admin-password-input"
          type="password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          className="mt-6 w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-3 rounded-none placeholder:text-white/30"
        />
        {error && <p data-testid="admin-login-error" className="text-red-400 text-sm mt-3">{error}</p>}
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={loading}
          className="mt-6 w-full bg-[#F2B705] text-black font-bold py-3 rounded-md hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {loading && <Loader2 className="animate-spin" size={18} />}
          {loading ? "Signing in..." : "Sign In"}
        </button>
        <Link to="/" className="block text-center text-white/40 text-sm mt-5 hover:text-[#F2B705]">← Back to site</Link>
      </form>
    </div>
  );
}

function ContactReply({ c, onReplied, onThread }) {
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState("");
  const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });
  const send = async (method) => {
    if (!msg.trim()) { toast.error("Type your reply first."); return; }
    setSending(method);
    try {
      const { data } = await axios.post(`${API}/contact/${c.id}/reply`, { method, message: msg }, authH());
      onReplied(data.reply);
      setMsg("");
      const ok = Object.entries(data.results).filter(([, v]) => v).map(([k]) => k.toUpperCase()).join(" + ");
      toast.success(`Reply sent via ${ok}.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send reply."); }
    finally { setSending(""); }
  };
  const syncReplies = async () => {
    setSending("sync");
    try {
      const { data } = await axios.post(`${API}/contact/${c.id}/sync-replies`, {}, authH());
      onThread(data.replies);
      toast.success(data.new ? `${data.new} new repl${data.new === 1 ? "y" : "ies"} pulled from GoHighLevel.` : "No new replies yet.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not check for replies."); }
    finally { setSending(""); }
  };
  const thread = [...(c.replies || [])].sort((a, b) => String(a.at || "").localeCompare(String(b.at || "")));
  return (
    <div className="mt-3 space-y-2">
      {thread.length > 0 && (
        <div className="space-y-1.5">
          {thread.map((rp, i) => rp.direction === "inbound" ? (
            <div key={rp.ghl_message_id || i} data-testid={`contact-inbound-row-${c.id}-${i}`} className="rounded-md border border-sky-400/30 bg-sky-400/[0.06] p-2.5 mr-8">
              <div className="text-[10px] uppercase tracking-widest text-sky-300/80">
                {(c.name || "Customer").split(" ")[0]} replied via {rp.method || "sms"} · {fmtDateTime(rp.at)}
              </div>
              <p className="text-white/80 text-sm whitespace-pre-wrap mt-1">{rp.message}</p>
            </div>
          ) : (
            <div key={i} data-testid={`contact-reply-row-${c.id}-${i}`} className="rounded-md border border-[#F2B705]/25 bg-[#F2B705]/[0.05] p-2.5 ml-8">
              <div className="text-[10px] uppercase tracking-widest text-[#F2B705]/80">
                {c.admin_composed && i === 0 ? "Your message via" : "Your reply via"} {rp.delivered || rp.method} · {fmtDateTime(rp.at)}{rp.by ? ` · ${rp.by}` : ""}
              </div>
              <p className="text-white/70 text-sm whitespace-pre-wrap mt-1">{rp.message}</p>
            </div>
          ))}
        </div>
      )}
      {c.ghl_contact_id && (
        <button data-testid={`contact-sync-replies-${c.id}`} onClick={syncReplies} disabled={!!sending}
          className="text-[10px] font-bold border border-white/20 text-white/50 px-2 py-1 rounded hover:border-sky-400 hover:text-sky-300 disabled:opacity-50">
          {sending === "sync" ? "Checking…" : "↻ Check for new replies"}
        </button>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <textarea data-testid={`contact-reply-input-${c.id}`} rows={2} value={msg} onChange={(e) => setMsg(e.target.value)}
          placeholder={`Reply to ${(c.name || "").split(" ")[0]}…`}
          className="flex-1 bg-[#0A0A0A] border border-white/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#F2B705]/60" />
        <div className="flex sm:flex-col gap-1.5">
          {c.phone && (
            <button data-testid={`contact-reply-sms-${c.id}`} onClick={() => send("sms")} disabled={!!sending}
              className="text-[11px] font-bold bg-[#F2B705] text-black px-3 py-1.5 rounded hover:bg-white disabled:opacity-50 whitespace-nowrap">
              {sending === "sms" ? "Sending…" : "Reply by Text"}
            </button>
          )}
          <button data-testid={`contact-reply-email-${c.id}`} onClick={() => send("email")} disabled={!!sending}
            className="text-[11px] font-bold border border-white/20 text-white/70 px-3 py-1.5 rounded hover:border-[#F2B705] hover:text-[#F2B705] disabled:opacity-50 whitespace-nowrap">
            {sending === "email" ? "Sending…" : "Reply by Email"}
          </button>
          {c.phone && (
            <button data-testid={`contact-reply-both-${c.id}`} onClick={() => send("both")} disabled={!!sending}
              className="text-[11px] font-bold border border-white/20 text-white/50 px-3 py-1.5 rounded hover:border-[#F2B705] hover:text-[#F2B705] disabled:opacity-50 whitespace-nowrap">
              {sending === "both" ? "Sending…" : "Text + Email"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


const DnrBadge = ({ item, id }) => item?.dnr ? (
  <span data-testid={`dnr-warning-${id}`} title={item.dnr_reason ? `Reason: ${item.dnr_reason}` : "On the Do-Not-Rent list"}
    className="ml-3 inline-flex items-center gap-1 text-[10px] uppercase font-black px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/50 animate-pulse">
    <Ban size={10} /> DO NOT RENT{item.dnr_reason ? ` — ${item.dnr_reason}` : ""}
  </span>
) : null;

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("review") ? "reservations" : "command");
  const [reviewFocusId, setReviewFocusId] = useState(() => new URLSearchParams(window.location.search).get("review") || null);
  const [receiptFor, setReceiptFor] = useState(null);
  useEffect(() => {
    const h = (e) => {
      setTab("bookings");
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="booking-row-${e.detail}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-2", "ring-[#F2B705]");
          setTimeout(() => el.classList.remove("ring-2", "ring-[#F2B705]"), 3500);
        } else {
          toast.error("Rental not in the active bookings list — it may be completed or cancelled.");
        }
      }, 500);
    };
    window.addEventListener("autohire:open-booking", h);
    return () => window.removeEventListener("autohire:open-booking", h);
  }, []);
  const [resNewCount, setResNewCount] = useState(0);
  const [approvalsCount, setApprovalsCount] = useState(0);
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/reservations/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setResNewCount(r.data.alerts?.new_requests || 0)).catch(() => {});
    Promise.all([
      axios.get(`${API}/extension-requests?status=pending`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => (r.data || []).length).catch(() => 0),
      axios.get(`${API}/rebooking/requests`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => (r.data || []).filter((x) => ["Pending Approval", "Offer Sent"].includes(x.status)).length).catch(() => 0),
    ]).then(([ext, reb]) => setApprovalsCount(ext + reb));
  }, [token, tab]);
  const [loading, setLoading] = useState(false);
  const [approveFor, setApproveFor] = useState(null);
  const [inspectFor, setInspectFor] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [msgSelected, setMsgSelected] = useState(new Set());
  const [msgQuery, setMsgQuery] = useState("");
  const [scheduledRefreshKey, setScheduledRefreshKey] = useState(0);
  const [msgDeleting, setMsgDeleting] = useState(false);
  const [resyncingId, setResyncingId] = useState(null);
  const [crmFilter, setCrmFilter] = useState("all");
  const [rollingOnly, setRollingOnly] = useState(false);
  const [crmMeta, setCrmMeta] = useState({ location_id: "", pipeline_name: "" });

  const resyncCrm = async (b) => {
    setResyncingId(b.id);
    try {
      const { data } = await axios.post(`${API}/bookings/${b.id}/resync-crm`, {}, authHeader());
      setBookings((prev) => prev.map((x) => x.id === b.id ? {
        ...x,
        ghl_opportunity_id: data.ghl_opportunity_id,
        ghl_opportunity_stage: data.ghl_opportunity_stage,
        ghl_opportunity_value: data.ghl_opportunity_value,
        ghl_last_sync_at: data.ghl_last_sync_at,
        ghl_last_sync_ok: data.ghl_last_sync_ok,
        ghl_last_sync_error: data.ghl_last_sync_error,
      } : x));
      toast.success("CRM opportunity re-synced to GoHighLevel.");
    } catch (e) {
      // reflect the failure on the chip immediately
      setBookings((prev) => prev.map((x) => x.id === b.id ? {
        ...x, ghl_last_sync_ok: false, ghl_last_sync_at: new Date().toISOString(),
        ghl_last_sync_error: e?.response?.data?.detail || "Re-sync failed",
      } : x));
      toast.error(e?.response?.data?.detail || "Could not re-sync to GoHighLevel.");
    } finally { setResyncingId(null); }
  };

  const authHeader = (t) => ({ headers: { Authorization: `Bearer ${t || token}` } });

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected((prev) => (prev.size === bookings.length ? new Set() : new Set(bookings.map((b) => b.id))));
  };

  const uncancelBooking = async (b) => {
    if (!window.confirm(`Restore ${b.name}'s ${b.vehicle} booking back to active? Their agreement(s) will be reinstated.`)) return;
    try {
      const { data } = await axios.post(`${API}/bookings/${b.id}/uncancel`, {}, authHeader());
      toast.success(`Booking restored — back to ${data.status}${data.agreements_restored ? ` · ${data.agreements_restored} agreement(s) reinstated` : ""}.`);
      load();
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error(err.response?.data?.detail || "Could not restore booking.");
    }
  };

  const cancelBooking = async (id) => {
    if (!window.confirm("Cancel this booking? This voids all agreements and frees the car for same-day rebooking. The record stays in your history.")) return;
    const reason = window.prompt("Reason for cancellation (optional):", "") || "";
    try {
      const { data } = await axios.post(`${API}/bookings/${id}/cancel`, { reason }, authHeader());
      toast.success(`Booking cancelled. ${data.vehicle_freed} is free to rebook${data.agreements_voided ? ` · ${data.agreements_voided} agreement(s) voided` : ""}.`);
      load();
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error(err.response?.data?.detail || "Could not cancel booking.");
    }
  };

  const reissueAgreement = async (id) => {
    if (!window.confirm("Re-issue the rental agreement with the latest dates/total? This voids the current agreement and texts/emails the customer a new signing link.")) return;
    try {
      const { data } = await axios.post(`${API}/bookings/${id}/reissue-agreement`, {}, authHeader());
      toast.success(`New agreement ${data.agreement_number} issued.${data.renter_notified ? " Signing link sent to customer." : ""}`);
      load();
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error(err.response?.data?.detail || "Could not re-issue agreement.");
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Delete this booking permanently? This also removes its agreement.")) return;
    try {
      await axios.delete(`${API}/bookings/${id}`, authHeader());
      toast.success("Booking deleted.");
      setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
      load();
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error(err.response?.data?.detail || "Could not delete booking.");
    }
  };

  const deleteSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} selected booking(s) permanently? This also removes their agreements.`)) return;
    setDeleting(true);
    try {
      const { data } = await axios.post(`${API}/bookings/bulk-delete`, { ids }, authHeader());
      toast.success(`${data.deleted} booking(s) deleted.`);
      setSelected(new Set());
      load();
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error(err.response?.data?.detail || "Could not delete bookings.");
    } finally {
      setDeleting(false);
    }
  };

  const toggleMsgSelect = (id) => {
    setMsgSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const filteredContacts = (() => {
    const q = msgQuery.trim().toLowerCase();
    if (!q) return contacts;
    const qDigits = q.replace(/\D/g, "");
    return contacts.filter((c) => (c.name || "").toLowerCase().includes(q)
      || (c.email || "").toLowerCase().includes(q)
      || (qDigits && (c.phone || "").replace(/\D/g, "").includes(qDigits)));
  })();

  const toggleMsgSelectAll = () => {
    setMsgSelected((prev) => (prev.size === filteredContacts.length ? new Set() : new Set(filteredContacts.map((c) => c.id))));
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Delete this message permanently?")) return;
    try {
      await axios.delete(`${API}/contact/${id}`, authHeader());
      toast.success("Message deleted.");
      setMsgSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
      load();
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error(err.response?.data?.detail || "Could not delete message.");
    }
  };

  const deleteMsgSelected = async () => {
    const visibleIds = new Set(filteredContacts.map((c) => c.id));
    const ids = Array.from(msgSelected).filter((id) => visibleIds.has(id));
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} selected message(s) permanently?`)) return;
    setMsgDeleting(true);
    try {
      const { data } = await axios.post(`${API}/contact/bulk-delete`, { ids }, authHeader());
      toast.success(`${data.deleted} message(s) deleted.`);
      setMsgSelected(new Set());
      load();
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error(err.response?.data?.detail || "Could not delete messages.");
    } finally {
      setMsgDeleting(false);
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  const load = useCallback(async (t) => {
    const h = { headers: { Authorization: `Bearer ${t || token}` } };
    setLoading(true);
    try {
      const [b, c, a] = await Promise.all([
        axios.get(`${API}/bookings`, h),
        axios.get(`${API}/contact`, h),
        axios.get(`${API}/agreements`, h),
      ]);
      setBookings(b.data);
      setContacts(c.data);
      setAgreements(a.data);
      axios.get(`${API}/system/ghl/crm-meta`, h).then((r) => setCrmMeta(r.data || {})).catch(() => {});
    } catch (err) {
      if (err.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  const openPdf = async (agreementId) => {
    try {
      const res = await axios.get(`${API}/agreements/${agreementId}/pdf`, { ...authHeader(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch {
      toast.error("Could not open PDF.");
    }
  };

  const resendLink = async (agreementId) => {
    try {
      const { data } = await axios.post(`${API}/agreements/${agreementId}/resend-link`, {}, authHeader());
      toast.success(`Signing link re-sent to ${data.sent_to}.`);
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error(err.response?.data?.detail || "Could not resend link.");
    }
  };

  const requestReview = async (agreementId) => {
    try {
      const { data } = await axios.post(`${API}/agreements/${agreementId}/request-review`, {}, authHeader());
      toast.success(`Review request sent to ${data.sent_to}.`);
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error(err.response?.data?.detail || "Could not send review request.");
    }
  };

  const markSigned = async (agreementId) => {
    if (!window.confirm("Mark this agreement as SIGNED? Use this only when the renter signed a hard copy in person. This finalizes the agreement and sets the rental to Active.")) return;
    try {
      await axios.post(`${API}/agreements/${agreementId}/mark-signed`, {}, authHeader());
      toast.success("Agreement marked as signed (hard copy on file).");
      load();
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error(err.response?.data?.detail || "Could not mark as signed.");
    }
  };

  const sendSelfServiceLink = async (bookingId) => {
    try {
      const { data } = await axios.post(`${API}/bookings/${bookingId}/self-service-link`, {}, authHeader());
      try { await navigator.clipboard.writeText(data.url); } catch { /* noop */ }
      const ch = [data.sent_email && "email", data.sent_sms && "text"].filter(Boolean).join(" & ");
      toast.success(`Self-service link sent${ch ? ` via ${ch}` : ""} and copied to clipboard.`);
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error(err.response?.data?.detail || "Could not send self-service link.");
    }
  };

  const toggleEscalation = async (b) => {
    const action = b.escalation_stopped ? "resume-escalation" : "stop-escalation";
    try {
      await axios.post(`${API}/bookings/${b.id}/${action}`, { reason: "Renter squared up" }, authHeader());
      toast.success(b.escalation_stopped ? "Overdue auto-texts resumed." : "Overdue auto-texts stopped for this rental.");
      load();
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error(err.response?.data?.detail || "Could not update escalation.");
    }
  };


  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const fmt = (s) => (s ? fmtDateTime(s) : "—");

  if (!token) return <LoginGate onAuthed={(t) => setToken(t)} />;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 sticky top-0 bg-[#050505]/90 backdrop-blur-xl z-10">
        <div className="mx-auto max-w-6xl px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-back" className="text-white/60 hover:text-[#F2B705] transition-colors"><ArrowLeft size={20} /></Link>
            <h1 className="font-display font-extrabold text-xl">AutoHire <span className="text-gold-gradient">STL</span> · Admin</h1>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell token={token} />
            <button data-testid="admin-refresh" onClick={() => load()} className="flex items-center gap-2 text-sm border border-white/15 px-3 sm:px-4 py-2 rounded-md hover:bg-white hover:text-black transition-colors">
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> <span className="hidden sm:inline">Refresh</span>
            </button>
            <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 text-sm border border-white/15 px-3 sm:px-4 py-2 rounded-md hover:bg-red-500 hover:border-red-500 transition-colors">
              <LogOut size={15} /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-8">
        <div className="mb-5">
          <AdminSearch onNavigate={(t) => { setTab(t); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
        </div>
        <DailyBriefingBanner token={token} onOpen={() => { setTab("assistant"); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
        <div className="flex flex-wrap gap-2 mb-6">
          <button data-testid="admin-tab-command" onClick={() => setTab("command")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "command" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <Gauge size={16} /> Command Center
          </button>
          <button data-testid="admin-tab-assistant" onClick={() => setTab("assistant")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "assistant" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <Bot size={16} /> AI Assistant
          </button>
          <button data-testid="admin-tab-dashboard" onClick={() => setTab("dashboard")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "dashboard" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <LayoutDashboard size={16} /> Dashboard
          </button>
          <button data-testid="admin-tab-bookings" onClick={() => setTab("bookings")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "bookings" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <Car size={16} /> Active Bookings ({bookings.filter((b) => b.status !== "cancelled" && !isCompletedRental(b)).length})
          </button>
          <button data-testid="admin-tab-reservations" onClick={() => setTab("reservations")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "reservations" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <ClipboardList size={16} /> Reservations{resNewCount > 0 && <span className="px-1.5 py-0.5 rounded-full bg-red-500 text-white text-xs font-bold">{resNewCount}</span>}
          </button>
          <button data-testid="admin-tab-completed" onClick={() => setTab("completed")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "completed" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <Archive size={16} /> Completed ({bookings.filter(isCompletedRental).length})
          </button>
          <button data-testid="admin-tab-cancelled" onClick={() => setTab("cancelled")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "cancelled" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <Ban size={16} /> Cancelled / Declined ({bookings.filter((b) => b.status === "cancelled").length})
          </button>
          <button data-testid="admin-tab-contacts" onClick={() => setTab("contacts")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "contacts" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <Mail size={16} /> Messages ({contacts.length})
          </button>
          <button data-testid="admin-tab-agreements" onClick={() => setTab("agreements")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "agreements" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <FileSignature size={16} /> Agreements ({agreements.length})
          </button>
          <button data-testid="admin-tab-fleet" onClick={() => setTab("fleet")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "fleet" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <Truck size={16} /> Fleet
          </button>
          <button data-testid="admin-tab-share" onClick={() => setTab("share")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "share" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <QrCode size={16} /> Share / QR
          </button>
          <button data-testid="admin-tab-reviews" onClick={() => setTab("reviews")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "reviews" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <Star size={16} /> Reviews
          </button>
          <button data-testid="admin-tab-money" onClick={() => setTab("money")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "money" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <DollarSign size={16} /> Money
          </button>
          <button data-testid="admin-tab-due-reminders" onClick={() => setTab("due-reminders")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "due-reminders" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <BellRing size={16} /> Due Reminders
          </button>
          <button data-testid="admin-tab-leakage" onClick={() => setTab("leakage")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "leakage" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <TrendingDown size={16} /> Revenue Leakage
          </button>
          <button data-testid="admin-tab-customers" onClick={() => setTab("customers")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "customers" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <Users size={16} /> Customers
          </button>
          <button data-testid="admin-tab-dnr" onClick={() => setTab("dnr")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "dnr" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <Ban size={16} className={tab === "dnr" ? "" : "text-red-400"} /> Do Not Rent
          </button>
          <button data-testid="admin-tab-rebooking" onClick={() => setTab("rebooking")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "rebooking" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <RefreshCw size={16} /> Rebooking{approvalsCount > 0 && <span data-testid="rebooking-approvals-badge" className="px-1.5 py-0.5 rounded-full bg-red-500 text-white text-xs font-bold">{approvalsCount}</span>}
          </button>
          <button data-testid="admin-tab-availability" onClick={() => setTab("availability")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "availability" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <CalendarDays size={16} /> Availability
          </button>
          <button data-testid="admin-tab-recovery" onClick={() => setTab("recovery")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "recovery" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <ShieldAlert size={16} /> Recovery
          </button>
          <button data-testid="admin-tab-marketing" onClick={() => setTab("marketing")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "marketing" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <Megaphone size={16} /> Marketing
          </button>
          <button data-testid="admin-tab-analytics" onClick={() => setTab("analytics")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "analytics" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <BarChart3 size={16} /> Traffic
          </button>
          <button data-testid="admin-tab-drivers" onClick={() => setTab("drivers")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "drivers" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <UserCheck size={16} /> Drivers
          </button>
          <button data-testid="admin-tab-citations" onClick={() => setTab("citations")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "citations" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <Receipt size={16} /> Citations
          </button>
          <button data-testid="admin-tab-rewards" onClick={() => setTab("rewards")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "rewards" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <Gift size={16} /> Rewards
          </button>
          <button data-testid="admin-tab-maintenance" onClick={() => setTab("maintenance")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "maintenance" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <Wrench size={16} /> Maintenance
          </button>
          <button data-testid="admin-tab-notifications" onClick={() => setTab("notifications")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "notifications" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <Bell size={16} /> Notifications
          </button>
          <button data-testid="admin-tab-automation" onClick={() => setTab("automation")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "automation" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <Power size={16} /> Automations
          </button>
          <button data-testid="admin-tab-pnl" onClick={() => setTab("pnl")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "pnl" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <Calculator size={16} /> Tax / P&amp;L
          </button>
          <button data-testid="admin-tab-imports" onClick={() => setTab("imports")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "imports" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <Upload size={16} /> Import
          </button>
          <button data-testid="admin-tab-inspections" onClick={() => setTab("inspections")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "inspections" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <Camera size={16} /> Inspections
          </button>
          <button data-testid="admin-tab-playbook" onClick={() => setTab("playbook")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "playbook" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <ClipboardList size={16} /> Playbook
          </button>
          <button data-testid="admin-tab-help" onClick={() => setTab("help")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "help" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <BookOpen size={16} /> Help
          </button>
          <button data-testid="admin-tab-system" onClick={() => setTab("system")} className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-colors ${tab === "system" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}>
            <Plug size={16} /> System
          </button>
        </div>

        {tab !== "help" && <TabManual tabKey={tab} token={token} />}

        {tab === "command" && <CommandCenter token={token} onAuthError={logout} onNavigate={setTab} />}
        {tab === "assistant" && <AiAssistant token={token} onNavigate={(t, focus) => { if (focus) setReviewFocusId(focus); setTab(t); window.scrollTo({ top: 0, behavior: "smooth" }); }} />}
        {tab === "dashboard" && <OperationsDashboard token={token} />}

        {tab === "bookings" && (
          <div className="space-y-3" data-testid="admin-bookings-list">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-black text-2xl">Bookings</h2>
              <ManualBooking token={token} onCreated={load} />
            </div>
            {bookings.length === 0 && <p className="text-white/40">No booking requests yet.</p>}
            {bookings.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 bg-[#0C0C0C] border border-white/10 rounded-lg px-4 py-3">
                <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer" data-testid="bookings-select-all-label">
                  <input
                    type="checkbox"
                    data-testid="bookings-select-all"
                    checked={selected.size === bookings.length && bookings.length > 0}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 accent-[#F2B705]"
                  />
                  {selected.size > 0 ? `${selected.size} selected` : "Select all"}
                </label>
                <div className="flex items-center gap-2 flex-wrap">
                  {(() => {
                    const today = new Date().toISOString().slice(0, 10);
                    const rolling = bookings.filter((b) => b.status !== "cancelled" && !isCompletedRental(b) && b.daily_rolling?.enabled);
                    const rollDue = rolling.filter((b) => (b.daily_rolling.paid_through || "").slice(0, 10) <= today).length;
                    if (!rolling.length && !rollingOnly) return null;
                    return (
                      <button data-testid="rolling-filter-toggle" onClick={() => setRollingOnly((v) => !v)}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-md border transition-colors ${rollingOnly ? "bg-[#F2B705] text-black border-[#F2B705]" : rollDue > 0 ? "border-red-500/50 text-red-300 hover:brightness-125" : "border-[#F2B705]/40 text-[#F2B705] hover:brightness-125"}`}>
                        🔄 Daily Rolling ({rolling.length}{rollDue > 0 ? ` — ${rollDue} DUE` : " — all paid up"})
                      </button>
                    );
                  })()}
                  {(() => {
                    const active = bookings.filter((b) => b.status !== "cancelled" && !isCompletedRental(b));
                    const counts = {
                      all: active.length,
                      synced: active.filter((b) => b.ghl_last_sync_ok === true).length,
                      "not-synced": active.filter((b) => !b.ghl_last_sync_at).length,
                      failed: active.filter((b) => b.ghl_last_sync_ok === false).length,
                    };
                    const opts = [
                      { key: "all", label: "All" },
                      { key: "synced", label: "Synced" },
                      { key: "not-synced", label: "Not Synced" },
                      { key: "failed", label: "Failed" },
                    ];
                    return (
                      <div className="inline-flex rounded-lg border border-white/10 bg-[#0A0A0A] p-0.5" data-testid="crm-filter-group">
                        {opts.map((o) => (
                          <button
                            key={o.key}
                            data-testid={`crm-filter-${o.key}`}
                            onClick={() => setCrmFilter(o.key)}
                            className={`text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${crmFilter === o.key ? (o.key === "failed" ? "bg-red-500 text-white" : "bg-[#F2B705] text-black") : "text-white/55 hover:text-white"} ${o.key === "failed" && counts.failed > 0 && crmFilter !== "failed" ? "text-red-300" : ""}`}
                          >
                            {o.label} <span className="opacity-70">({counts[o.key]})</span>
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                  {selected.size > 0 && (
                    <button
                      data-testid="bookings-delete-selected"
                      onClick={deleteSelected}
                      disabled={deleting}
                      className="flex items-center gap-2 text-sm bg-red-500/15 text-red-400 border border-red-500/30 px-4 py-2 rounded-md hover:bg-red-500 hover:text-white transition-colors disabled:opacity-60"
                    >
                      {deleting ? <Loader2 className="animate-spin" size={15} /> : <Trash2 size={15} />} Delete Selected ({selected.size})
                    </button>
                  )}
                </div>
              </div>
            )}
            {bookings.filter((b) => b.status !== "cancelled" && !isCompletedRental(b)).filter((b) => {
              if (rollingOnly && !b.daily_rolling?.enabled) return false;
              if (crmFilter === "synced") return b.ghl_last_sync_ok === true;
              if (crmFilter === "failed") return b.ghl_last_sync_ok === false;
              if (crmFilter === "not-synced") return !b.ghl_last_sync_at;
              return true;
            }).sort((a, z) => {
              if (!rollingOnly) return 0;
              const t = new Date().toISOString().slice(0, 10);
              const due = (b) => ((b.daily_rolling?.paid_through || "").slice(0, 10) <= t ? 0 : 1);
              return due(a) - due(z) || String(a.daily_rolling?.paid_through || "").localeCompare(String(z.daily_rolling?.paid_through || ""));
            }).map((b) => (
              <BookingCard
                key={b.id}
                b={b}
                token={token}
                selected={selected.has(b.id)}
                agreement={agreements.find((a) => a.id === b.agreement_id) || agreements.find((a) => a.booking_id === b.id)}
                crmMeta={crmMeta}
                resyncing={resyncingId === b.id}
                fmt={fmt}
                actions={{ toggleSelect, deleteBooking, resyncCrm, load, logout, setApproveFor, setReceiptFor, reissueAgreement, cancelBooking, sendSelfServiceLink, toggleEscalation, openPdf, resendLink, markSigned }}
              />
            ))}
          </div>
        )}

        {tab === "contacts" && (
          <div className="space-y-3" data-testid="admin-contacts-list">
            <div className="flex flex-wrap items-center justify-between gap-3 -mt-2 mb-1">
              <p className="text-white/45 text-sm">Messages submitted through your website's Contact form, plus any you start yourself below.</p>
              <div className="flex flex-wrap items-center gap-2">
                <BulkMessage token={token} onDone={() => load()} />
                <ComposeMessage token={token} onSent={(c) => setContacts((prev) => [c, ...prev])}
                  onScheduled={() => setScheduledRefreshKey((k) => k + 1)} />
              </div>
            </div>
            <ScheduledMessages token={token} refreshKey={scheduledRefreshKey} />
            {contacts.length > 0 && (
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                <input data-testid="messages-search" value={msgQuery}
                  onChange={(e) => { setMsgQuery(e.target.value); setMsgSelected(new Set()); }}
                  placeholder="Search by name, email or phone…"
                  className="w-full bg-[#0C0C0C] border border-white/10 rounded-md pl-9 pr-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F2B705]/50" />
              </div>
            )}
            {contacts.length === 0 && <p className="text-white/40">No messages yet.</p>}
            {contacts.length > 0 && filteredContacts.length === 0 && (
              <p data-testid="messages-search-empty" className="text-white/40 text-sm">No messages match "{msgQuery}".</p>
            )}
            {filteredContacts.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 bg-[#0C0C0C] border border-white/10 rounded-lg px-4 py-3">
                <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="messages-select-all"
                    checked={msgSelected.size === filteredContacts.length && filteredContacts.length > 0}
                    onChange={toggleMsgSelectAll}
                    className="h-4 w-4 accent-[#F2B705]"
                  />
                  {msgSelected.size > 0 ? `${msgSelected.size} selected` : "Select all"}
                </label>
                {msgSelected.size > 0 && (
                  <button
                    data-testid="messages-delete-selected"
                    onClick={deleteMsgSelected}
                    disabled={msgDeleting}
                    className="flex items-center gap-2 text-sm bg-red-500/15 text-red-400 border border-red-500/30 px-4 py-2 rounded-md hover:bg-red-500 hover:text-white transition-colors disabled:opacity-60"
                  >
                    {msgDeleting ? <Loader2 className="animate-spin" size={15} /> : <Trash2 size={15} />} Delete Selected ({msgSelected.size})
                  </button>
                )}
              </div>
            )}
            {filteredContacts.map((c) => (
              <div key={c.id} data-testid={`contact-row-${c.id}`} className={`bg-[#0C0C0C] border rounded-lg p-5 ${msgSelected.has(c.id) ? "border-[#F2B705]/50" : "border-white/10"}`}>
                <div className="flex flex-wrap justify-between gap-2">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      data-testid={`contact-select-${c.id}`}
                      checked={msgSelected.has(c.id)}
                      onChange={() => toggleMsgSelect(c.id)}
                      className="h-4 w-4 mt-1.5 accent-[#F2B705] shrink-0"
                    />
                    <div>
                      <span className="font-display font-bold text-lg">{c.name}</span>
                      <DnrBadge item={c} id={`msg-${c.id}`} />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-white/40">{fmt(c.created_at)}</span>
                    <button
                      data-testid={`contact-delete-${c.id}`}
                      onClick={() => deleteContact(c.id)}
                      title="Delete message"
                      className="text-white/40 hover:text-red-400 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm">
                  {c.email && <a href={`mailto:${c.email}`} className="text-white/70 hover:text-[#F2B705] transition-colors">✉️ {c.email}</a>}
                  {c.phone && <a href={`tel:${c.phone}`} className="text-[#F2B705] hover:underline">📞 {c.phone}</a>}
                  {c.admin_composed && <span className="px-2 py-0.5 rounded-full bg-[#F2B705]/10 text-[#F2B705] text-[10px] font-semibold">Started by you</span>}
                </div>
                {!c.admin_composed && (
                  <div className="mt-3 rounded-md bg-[#0A0A0A] border border-white/10 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Message</div>
                    <p className="text-white/75 text-sm whitespace-pre-wrap">{c.message?.trim() ? c.message : <span className="text-white/35 italic">No message was left.</span>}</p>
                  </div>
                )}
                <ContactReply c={c}
                  onReplied={(reply) => setContacts((prev) => prev.map((x) => (x.id === c.id ? { ...x, replies: [...(x.replies || []), reply] } : x)))}
                  onThread={(replies) => setContacts((prev) => prev.map((x) => (x.id === c.id ? { ...x, replies } : x)))} />
              </div>
            ))}
          </div>
        )}

        {tab === "agreements" && (
          <div className="space-y-3" data-testid="admin-agreements-list">
            <div className="flex flex-wrap items-center justify-between gap-3 bg-[#0C0C0C] border border-[#F2B705]/25 rounded-lg px-4 py-3">
              <div>
                <p className="font-semibold text-[#F2B705] flex items-center gap-2"><FileText size={16} /> Master Rental Agreement (blank)</p>
                <p className="text-white/45 text-xs mt-0.5">The official blank template for court, law enforcement, or printing.</p>
              </div>
              <a data-testid="master-agreement-btn" href={`${API}/master-agreement.pdf`} target="_blank" rel="noreferrer"
                className="flex items-center gap-2 bg-[#F2B705] text-black text-sm font-bold px-4 py-2 rounded-md hover:bg-white transition-colors">
                <Download size={15} /> View / Print Master Copy
              </a>
            </div>
            {agreements.length === 0 && <p className="text-white/40">No agreements yet. Approve a booking to generate one.</p>}
            {agreements.map((a) => (
              <div key={a.id} className="bg-[#0C0C0C] border border-white/10 rounded-lg p-5">
                <div className="flex flex-wrap justify-between gap-2">
                  <div>
                    <span className="font-display font-bold text-lg">{a.agreement_number}</span>
                    <span className="ml-3 text-white/60">{a.renter_name}</span>
                    <DnrBadge item={a} id={`agr-${a.id}`} />
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full ${a.status === "signed" ? "bg-green-500/15 text-green-400" : "bg-yellow-500/15 text-yellow-400"}`}>
                    {a.status === "signed" ? "Signed" : "Pending signature"}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-white/65">
                  <span>{a.vehicle_year} {a.vehicle_make} {a.vehicle_model}</span>
                  <span>🗓 {a.rental_start_date} → {a.rental_return_date}</span>
                  <span>{a.total_charges} · Bal {a.balance_due}</span>
                  {a.signed_at && <span className="text-green-400">Signed {fmt(a.signed_at)}</span>}
                </div>
                {a.ip_address && <p className="text-white/35 text-xs mt-1">Audit: IP {a.ip_address} · {(a.device_info || "").slice(0, 50)}</p>}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button data-testid={`agreement-pdf-${a.id}`} onClick={() => openPdf(a.id)} className="flex items-center gap-2 bg-[#F2B705] text-black text-sm font-bold px-4 py-2 rounded-md hover:bg-white transition-colors">
                    <FileText size={15} /> View Full Contract (PDF)
                  </button>
                  {a.status === "signed" ? (
                    <>
                      <button data-testid={`agreement-download-${a.id}`} onClick={() => openPdf(a.id)} className="flex items-center gap-2 border border-white/15 text-sm px-4 py-2 rounded-md hover:bg-white hover:text-black transition-colors">
                        <Download size={15} /> Download Signed PDF
                      </button>
                      <button data-testid={`agreement-request-review-${a.id}`} onClick={() => requestReview(a.id)} className="flex items-center gap-2 border border-[#F2B705]/30 text-[#F2B705] text-sm px-4 py-2 rounded-md hover:bg-[#F2B705] hover:text-black transition-colors">
                        <Star size={15} /> Request Review
                      </button>
                    </>
                  ) : (                    <>
                      <button data-testid={`agreement-resend-${a.id}`} onClick={() => resendLink(a.id)}
                        className="flex items-center gap-2 bg-[#F2B705]/15 text-[#F2B705] border border-[#F2B705]/30 text-sm px-4 py-2 rounded-md hover:bg-[#F2B705] hover:text-black transition-colors">
                        <Send size={15} /> Resend signing link
                      </button>
                      <button data-testid={`agreement-copy-link-${a.id}`} onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/sign/${a.token}`); toast.success("Signing link copied!"); }}
                        className="flex items-center gap-2 border border-white/15 text-sm px-4 py-2 rounded-md hover:bg-white hover:text-black transition-colors">
                        <Copy size={15} /> Copy signing link
                      </button>
                      <button data-testid={`agreement-mark-signed-${a.id}`} onClick={() => markSigned(a.id)}
                        className="flex items-center gap-2 border border-green-500/40 text-green-400 text-sm px-4 py-2 rounded-md hover:bg-green-500 hover:text-black transition-colors">
                        <Check size={15} /> Mark as Signed (hard copy)
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "fleet" && <FleetManager token={token} onAuthError={logout} />}

        {tab === "share" && <SharePanel />}

        {tab === "reviews" && <ReviewsManager token={token} onAuthError={logout} />}

        {tab === "money" && <FinanceCenter token={token} onAuthError={logout} />}
        {tab === "due-reminders" && <PaymentDueCycles token={token} onAuthError={logout} />}

        {tab === "leakage" && <RevenueLeakage token={token} onAuthError={logout} />}

        {tab === "customers" && <CustomersManager token={token} onAuthError={logout} />}
        {tab === "dnr" && <DnrManager token={token} onAuthError={logout} />}
        {tab === "rebooking" && <RebookingRequests token={token} onAuthError={logout} />}

        {tab === "availability" && <AvailabilityManager token={token} onAuthError={logout} />}
        {tab === "recovery" && <RecoveryManager token={token} onAuthError={logout} />}
        {tab === "analytics" && <AnalyticsManager token={token} onAuthError={logout} />}
        {tab === "drivers" && <DriversManager token={token} onAuthError={logout} />}
        {tab === "citations" && <CitationsManager token={token} onAuthError={logout} />}
        {tab === "rewards" && <RewardsManager token={token} />}
        {tab === "marketing" && <MarketingCenter token={token} onNavigate={setTab} />}
        {tab === "reservations" && <ReservationQueue token={token} focusId={reviewFocusId} onStatsChanged={(s) => setResNewCount(s?.alerts?.new_requests || 0)} />}
        {tab === "completed" && <CompletedRentals bookings={bookings.filter(isCompletedRental)} token={token} onRefresh={() => load()} />}

        {tab === "cancelled" && (
          <div className="space-y-3" data-testid="admin-cancelled-list">
            <h2 className="font-display font-black text-2xl flex items-center gap-2"><Ban className="text-red-400" size={22} /> Cancelled Bookings</h2>
            {bookings.filter((b) => b.status === "cancelled").length === 0 ? (
              <div className="text-center py-12 border border-white/10 rounded-xl bg-[#0A0A0A] text-white/50">No cancelled bookings.</div>
            ) : (
              bookings.filter((b) => b.status === "cancelled").map((b) => (
                <div key={b.id} data-testid={`cancelled-row-${b.id}`} className="bg-[#0C0C0C] border border-red-500/20 rounded-lg p-5">
                  <div className="flex flex-wrap justify-between gap-2">
                    <div>
                      <span className="font-display font-bold text-lg">{b.name}</span>
                      <span className="ml-3 text-[#F2B705] font-semibold">{b.vehicle}</span>
                      <span className="ml-3 text-xs text-red-400 border border-red-500/40 rounded px-2 py-0.5 bg-red-500/[0.08]">Cancelled</span>
                    </div>
                    <span className="text-white/45 text-sm">{b.email} · {b.phone}</span>
                  </div>
                  <div className="mt-2 text-sm text-white/55 flex flex-wrap gap-x-4 gap-y-1">
                    <span>🗓 {b.pickup_date} → {b.return_date}</span>
                    {b.cancelled_at && <span>Cancelled {String(b.cancelled_at).slice(0, 10)}</span>}
                    {b.cancelled_by && <span className="text-amber-400">By: {b.cancelled_by}</span>}
                    {b.cancel_reason && <span>Reason: {b.cancel_reason}</span>}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button data-testid={`restore-cancelled-${b.id}`} onClick={() => uncancelBooking(b)}
                      className="text-xs flex items-center gap-1.5 bg-emerald-500/15 border border-emerald-500 text-emerald-300 font-bold px-3 py-1.5 rounded hover:bg-emerald-500 hover:text-black transition-colors">
                      <RotateCcw size={13} /> Restore Booking
                    </button>
                    <button data-testid={`delete-cancelled-${b.id}`} onClick={() => deleteBooking(b.id)}
                      className="text-xs flex items-center gap-1.5 border border-red-500/40 text-red-400 px-3 py-1.5 rounded hover:bg-red-500 hover:text-white transition-colors">
                      <Trash2 size={13} /> Delete permanently
                    </button>
                  </div>
                </div>
              ))
            )}
            <DeclinedRequests token={token} />
          </div>
        )}

        {tab === "maintenance" && <FleetMaintenance token={token} onAuthError={logout} />}
        {tab === "notifications" && <NotificationsHealth token={token} onAuthError={logout} />}
        {tab === "automation" && <AutomationCenter token={token} onAuthError={logout} />}
        {tab === "pnl" && <TaxPnL token={token} onAuthError={logout} />}
        {tab === "imports" && <ImportContacts token={token} onAuthError={logout} />}
        {tab === "inspections" && <InspectionsTab token={token} onAuthError={logout} />}
        {tab === "playbook" && <CheckoutPlaybook />}
        {tab === "help" && <HelpManual token={token} />}
        {tab === "system" && <SystemCenter token={token} onAuthError={logout} onNavigate={setTab} />}
        {receiptFor && <ReceiptDialog booking={receiptFor} token={token} onClose={() => setReceiptFor(null)} />}
      </div>
      {approveFor && <ApproveModal booking={approveFor} token={token} onClose={() => setApproveFor(null)} onDone={() => { setApproveFor(null); load(); setTab("agreements"); }} />}
    </div>
  );
}

function ApproveModal({ booking, token, onClose, onDone }) {
  const [f, setF] = useState({ vehicle_year: "", vehicle_make: booking.vehicle || "", vehicle_model: "", vin: "", license_plate: "", odometer_out: "", dl_number: booking.dl_number || "", dl_state: booking.dl_state || "MO", dl_expiration: booking.dl_expiration || "", taxes: 0, discounts: 0, amount_paid: 0 });
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const cls = "w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2 text-sm rounded-none";

  // Auto-fill vehicle details from the matched fleet record
  useEffect(() => {
    axios.get(`${API}/vehicles?all=true`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        const target = (booking.vehicle || "").toLowerCase().trim();
        const v = data.find((x) => `${x.make} ${x.model}`.toLowerCase().trim() === target)
          || data.find((x) => target.includes((x.model || "").toLowerCase()) && (x.model || ""));
        if (v) setF((p) => ({ ...p, vehicle_year: p.vehicle_year || v.year || "", vehicle_make: v.make || p.vehicle_make, vehicle_model: p.vehicle_model || v.model || "", license_plate: p.license_plate || v.license_plate || "" }));
      })
      .catch(() => {});
  }, [booking.vehicle, token]);

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/approve`, f, { headers: { Authorization: `Bearer ${token}` } });
      const url = `${window.location.origin}/sign/${data.token}`;
      setLink(url);
      toast.success(`Agreement ${data.agreement_number} created & emailed to ${booking.email}.`);
    } catch {
      toast.error("Could not approve. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0C0C0C] border border-white/12 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="approve-modal">
        <h3 className="font-display font-bold text-xl">Approve & Generate Agreement</h3>
        <p className="text-white/50 text-sm mt-1">{booking.name} · {booking.vehicle}</p>
        {link ? (
          <div className="mt-5 text-center">
            <p className="text-green-400 text-sm">✓ Agreement created and the signing link was automatically emailed to <b>{booking.email}</b>. No action needed — you can also copy the backup link below.</p>
            <div className="mt-3 bg-[#0A0A0A] border border-white/10 rounded-md p-3 text-xs break-all text-[#F2B705]">{link}</div>
            <button data-testid="approve-copy-backup-link" onClick={() => { navigator.clipboard?.writeText(link); toast.success("Backup link copied."); }} className="mt-3 text-xs text-white/50 hover:text-[#F2B705]">Copy backup link</button>
            <div><button onClick={onDone} className="mt-4 bg-[#F2B705] text-black font-bold px-6 py-2.5 rounded-md hover:bg-white transition-colors">Done</button></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 mt-5">
              <div><label className="text-xs text-white/45">Year</label><input className={cls} value={f.vehicle_year} onChange={(e) => set("vehicle_year", e.target.value)} /></div>
              <div><label className="text-xs text-white/45">Make</label><input className={cls} value={f.vehicle_make} onChange={(e) => set("vehicle_make", e.target.value)} /></div>
              <div><label className="text-xs text-white/45">Model</label><input className={cls} value={f.vehicle_model} onChange={(e) => set("vehicle_model", e.target.value)} /></div>
              <div><label className="text-xs text-white/45">License Plate</label><input className={cls} value={f.license_plate} onChange={(e) => set("license_plate", e.target.value)} /></div>
              <div className="col-span-2"><label className="text-xs text-white/45">VIN</label><input className={cls} value={f.vin} onChange={(e) => set("vin", e.target.value)} /></div>
              <div><label className="text-xs text-white/45">Odometer Out</label><input className={cls} value={f.odometer_out} onChange={(e) => set("odometer_out", e.target.value)} /></div>
              <div><label className="text-xs text-white/45">DL Number</label><input className={cls} value={f.dl_number} onChange={(e) => set("dl_number", e.target.value)} /></div>
              <div><label className="text-xs text-white/45">DL State</label><input className={cls} value={f.dl_state} onChange={(e) => set("dl_state", e.target.value)} /></div>
              <div><label className="text-xs text-white/45">DL Expiration</label><input type="date" className={`${cls} [color-scheme:dark]`} value={f.dl_expiration} onChange={(e) => set("dl_expiration", e.target.value)} /></div>
              <div><label className="text-xs text-white/45">Taxes ($)</label><input type="number" className={cls} value={f.taxes} onChange={(e) => set("taxes", parseFloat(e.target.value) || 0)} /></div>
              <div><label className="text-xs text-white/45">Discounts ($)</label><input type="number" className={cls} value={f.discounts} onChange={(e) => set("discounts", parseFloat(e.target.value) || 0)} /></div>
              <div><label className="text-xs text-white/45">Amount Paid ($)</label><input type="number" className={cls} value={f.amount_paid} onChange={(e) => set("amount_paid", parseFloat(e.target.value) || 0)} /></div>
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={onClose} className="flex-1 border border-white/15 py-2.5 rounded-md hover:bg-white/5">Cancel</button>
              <button data-testid="approve-submit" onClick={submit} disabled={loading} className="flex-1 bg-[#F2B705] text-black font-bold py-2.5 rounded-md hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {loading && <Loader2 className="animate-spin" size={16} />} Create Agreement
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
